import { useState, useEffect, useRef } from "react";
import {
  FaSearch,
  FaRegEdit,
  FaRegTrashAlt,
  FaLock,
  FaPlus,
} from "react-icons/fa";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import CreateUserModal from "./CreateUserForm";
import PasswordChangeModal from "./changeUserPassword";
import {
  usertableData,
  deleteUser,
} from "./../../../../services/userMangement/userManagement";
import { lastLoginBodyTemplate } from "../../../../utility/helper";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { handleErrorRedirect } from "../../../../utility/errorRedirect";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../../../components/Stepper/fields/inputField";

const statusBodyTemplate = (rowData) => {
  return (
    <span
      className={`px-3 py-1 rounded-full text-white text-xs
    ${
      rowData.is_active
        ? "bg-green-600/80 dark:bg-green-500/50 bg-green-500/50 backdrop-blur-md backdrop-saturate-150 border border-green-300/50 shadow-md"
        : "bg-red-500/40 backdrop-blur-md backdrop-saturate-150 border border-red-300/50 shadow-md"
    }
  `}
    >
      {rowData.is_active ? "Enable" : "Disable"}
    </span>
  );
};

const UserManagement = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [passWordModalVisible, setPassWordModalVisible] = useState(false);

  const [type, setType] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});
  const toast = useRef(null);
  const navigate = useNavigate();

  const openConfirmDialog = (config) => {
    setDialogConfig(config);
    setIsDialogOpen(true);
  };

  const confirmDelete = (user) => {
    openConfirmDialog({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this user?",
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelClass:
        "ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium border dark:border-gray-300 border-info rounded-md bg-transparent text-info dark:text-grey rounded-md focus:outline-none",
      confirmClass:
        "ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium dark:text-black dark:button-primary-gradient rounded-md bg-lightPrimary text-white rounded-md focus:outline-none",
      onConfirm: () => handleDelete(user),
    });
  };

  const handleDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem("access_token"));
      const response = await deleteUser(accessToken, user?.id);
      if (response) {
        await fetchUsers();
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: "User deleted successfully",
          life: 3000,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      handleErrorRedirect(status, navigate);
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: "Failed to delete user",
        life: 3000,
      });
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex justify-end gap-2">
        <button
          title="Edit User"
          onClick={() => {
            setModalVisible(true);
            setType("edit");
            setSelectedUser(rowData);
          }}
          className="text-primary"
        >
          <FaRegEdit />
        </button>
        <button
          title="Change User Password"
          onClick={() => {
            setPassWordModalVisible(true);
            setType("edit");
            setSelectedUser(rowData);
          }}
        >
          <FaLock className="text-info dark:text-white" />
        </button>
        <button
          title="Delete User"
          onClick={() => confirmDelete(rowData)}
          className="text-darkWaring"
        >
          <FaRegTrashAlt />
        </button>
      </div>
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, limit, page]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = JSON.parse(localStorage.getItem("access_token"));
      const response = await usertableData(
        accessToken,
        searchQuery,
        limit,
        page
      );

      if (response.status === 200) {
        setUsers(response?.data);
      } else {
        throw new Error("Failed to fetch user data");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
      const status = err?.response?.status;
      handleErrorRedirect(status, navigate);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setPage(0);
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        {...dialogConfig}
      />

      <div className="flex items-center px-4 rounded gap-2 mt-2">
        <span className="text-2xl text-lightPrimary dark:text-primary-gradient">
          User Management
        </span>
      </div>

      <div className="p-4">
        <h2 className="text-xl mb-4 dark:text-white text-info">Users List</h2>

        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center border border-gray-600 dark:text-white text-info px-4 py-1 rounded-md">
            <FaSearch className="mr-2 " />
            <form autoComplete="off">
              <input
                type="text"
                name="searchUserInput"
                placeholder="Search by name or email"
                className="bg-transparent outline-none w-full"
                value={searchQuery || ""}
                onChange={handleSearchChange}
                autoComplete="off"
              />
            </form>
          </div>
          <div>
            <button
              type="button"
              className="ml-6 md:ml-0 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium dark:text-black bg-lightPrimary text-white dark:button-primary-gradient rounded-md hover:bg-gray-100 focus:outline-none"
              onClick={() => {
                setModalVisible(true);
                setType("create");
              }}
            >
              <FaPlus /> <span className="hidden sm:block">Create New</span>
            </button>
          </div>
        </div>
        {error && <p className="text-red-500">{error}</p>}

        <DataTable
          lazy
          value={users?.users}
          stripedRows
          className="userTable"
          // paginator
          rows={limit}
          totalRecords={users?.total}
          first={page * limit}
          onPage={(e) => {
            setPage(e.page);
            setLimit(e.rows);
          }}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="name"
            header="Full Name"
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-white font-light"
          />
          <Column
            field="email"
            header="Email Address"
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-white font-light"
          />
          <Column
            field="last_login_at"
            header="Last Logged In"
            body={lastLoginBodyTemplate}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-white font-light"
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-white font-light"
          />
          <Column
            header="Actions"
            body={actionBodyTemplate}
            align={"right"}
            headerClassName="text-xl font-normal !text-lightPrimary dark:!text-primary-gradient"
            bodyClassName="text-base text-lightPrimary dark:text-white font-light"
          />
        </DataTable>
        <Pagination
          page={page}
          limit={limit}
          total={users?.total || 0}
          onPageChange={(newPage) => setPage(newPage)}
        />

        <CreateUserModal
          visible={modalVisible}
          fetchUsers={fetchUsers}
          onHide={() => {
            setModalVisible(false);
          }}
          type={type}
          toast={toast}
          selectedUser={selectedUser}
          loading={loading}
          setLoading={setLoading}
        />
        <PasswordChangeModal
          visible={passWordModalVisible}
          fetchUsers={fetchUsers}
          onHide={() => {
            setPassWordModalVisible(false);
          }}
          type={type}
          toast={toast}
          selectedUser={selectedUser}
          loading={loading}
          setLoading={setLoading}
        />
      </div>
    </>
  );
};

export default UserManagement;
